import React, { useEffect, useState } from 'react';
import { api } from './auth';

export default function AdminRequirements({ user, onChange }) {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(user.role === 'super_admin' ? '' : user.company_id);
  const [requirements, setRequirements] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (user.role === 'super_admin') {
      api.get('/admin/companies').then(r => {
        setCompanies(r.data);
        if (!companyId && r.data.length > 0) setCompanyId(r.data[0].id);
      });
    }
  }, []);

  const loadReqs = async () => {
    if (!companyId) return;
    const r = await api.get(`/admin/companies/${companyId}/requirements`);
    setRequirements(r.data);
    if (onChange) onChange();
  };
  useEffect(() => { loadReqs(); }, [companyId]);

  const importDoc = async (e) => {
    e.preventDefault();
    if (!importName) { alert('Give the requirement a name'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('name', importName);
      if (importFile) fd.append('file', importFile);
      else if (importText) fd.append('text_content', importText);
      else { alert('Provide a file or paste text'); setImporting(false); return; }
      await api.post(`/admin/companies/${companyId}/requirements/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowImport(false); setImportText(''); setImportFile(null); setImportName('');
      loadReqs();
    } finally { setImporting(false); }
  };

  const newBlank = () => setEditing({ name: '', is_default: false, fields_json: [], voice_checklist_json: [], written_checklist_json: [] });

  const save = async () => {
    if (!editing.name) { alert('Name required'); return; }
    const payload = {
      name: editing.name, is_default: editing.is_default,
      fields_json: editing.fields_json,
      voice_checklist_json: editing.voice_checklist_json,
      written_checklist_json: editing.written_checklist_json,
    };
    if (editing.id) await api.patch(`/admin/requirements/${editing.id}`, payload);
    else await api.post(`/admin/companies/${companyId}/requirements`, payload);
    setEditing(null); loadReqs();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this requirement set?')) return;
    await api.delete(`/admin/requirements/${id}`);
    loadReqs();
  };

  const updateList = (key, idx, field, value) => {
    const list = [...editing[key]];
    list[idx] = { ...list[idx], [field]: value };
    setEditing({ ...editing, [key]: list });
  };
  const addItem = (key) => setEditing({ ...editing, [key]: [...(editing[key] || []), { key: '', label: '' }] });
  const removeItem = (key, idx) => setEditing({ ...editing, [key]: editing[key].filter((_, i) => i !== idx) });

  if (!companyId && user.role === 'super_admin') {
    return <div style={card}><p style={{ color: '#9CA3AF' }}>Create a company first.</p></div>;
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Mandate requirements</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {user.role === 'super_admin' && (
            <select value={companyId} onChange={e => setCompanyId(parseInt(e.target.value))} style={inp}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button onClick={() => setShowImport(true)} style={btnGhost}>Import from doc</button>
          <button onClick={newBlank} style={btnPrimary}>+ New ruleset</button>
        </div>
      </div>

      {showImport && (
        <form onSubmit={importDoc} style={{ background: '#F8F9FB', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 10px 0' }}>Upload a PDF/text document or paste the requirements — Claude will extract the field schema.</p>
          <input placeholder="Ruleset name" value={importName} onChange={e => setImportName(e.target.value)} required style={{ ...inp, width: '100%', marginBottom: 10 }} />
          <input type="file" accept=".pdf,.txt,.md" onChange={e => setImportFile(e.target.files[0])} style={{ marginBottom: 10, display: 'block' }} />
          <textarea placeholder="…or paste requirements text here" value={importText} onChange={e => setImportText(e.target.value)} rows={6} style={{ ...inp, width: '100%', fontFamily: 'monospace' }} />
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={importing} style={btnPrimary}>{importing ? 'Parsing…' : 'Generate ruleset'}</button>
            <button type="button" onClick={() => setShowImport(false)} style={btnGhost}>Cancel</button>
          </div>
        </form>
      )}

      {editing && (
        <div style={{ background: '#F8F9FB', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{editing.id ? 'Edit ruleset' : 'New ruleset'}</h3>
          <input placeholder="Ruleset name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={{ ...inp, width: '100%', marginBottom: 10 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13 }}>
            <input type="checkbox" checked={editing.is_default} onChange={e => setEditing({ ...editing, is_default: e.target.checked })} />
            Use as default for this company
          </label>

          <ListEditor title="Mandate fields" items={editing.fields_json || []} onUpdate={(i, k, v) => updateList('fields_json', i, k, v)} onAdd={() => addItem('fields_json')} onRemove={i => removeItem('fields_json', i)} />
          <ListEditor title="Voice checklist" items={editing.voice_checklist_json || []} onUpdate={(i, k, v) => updateList('voice_checklist_json', i, k, v)} onAdd={() => addItem('voice_checklist_json')} onRemove={i => removeItem('voice_checklist_json', i)} />
          <ListEditor title="Written checklist" items={editing.written_checklist_json || []} onUpdate={(i, k, v) => updateList('written_checklist_json', i, k, v)} onAdd={() => addItem('written_checklist_json')} onRemove={i => removeItem('written_checklist_json', i)} />

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={save} style={btnPrimary}>Save ruleset</button>
            <button onClick={() => setEditing(null)} style={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {requirements.length === 0 && !editing
        ? <p style={{ color: '#9CA3AF' }}>No rulesets yet — defaults will be used.</p>
        : (
          <table style={tbl}>
            <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
              <th style={th}>Name</th><th style={th}>Default</th><th style={th}>Fields</th><th style={th}>Voice</th><th style={th}>Written</th><th style={th}>Updated</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {requirements.map(r => (
                <tr key={r.id} style={{ borderBottom: '0.5px solid #F3F4F6' }}>
                  <td style={td}><strong>{r.name}</strong></td>
                  <td style={td}>{r.is_default ? <span style={{ color: '#16A34A', fontWeight: 600, fontSize: 12 }}>✓ Default</span> : '—'}</td>
                  <td style={td}>{r.fields_json?.length || 0}</td>
                  <td style={td}>{r.voice_checklist_json?.length || 0}</td>
                  <td style={td}>{r.written_checklist_json?.length || 0}</td>
                  <td style={td}>{new Date(r.updated_at).toLocaleDateString()}</td>
                  <td style={td}>
                    <button onClick={() => setEditing(r)} style={{ ...btnGhost, fontSize: 11, padding: '4px 8px' }}>Edit</button>
                    <button onClick={() => del(r.id)} style={{ ...btnGhost, fontSize: 11, padding: '4px 8px', marginLeft: 4, color: '#DC2626' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

function ListEditor({ title, items, onUpdate, onAdd, onRemove }) {
  return <div style={{ marginBottom: 14 }}>
    <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>{title} ({items.length})</p>
    {items.map((it, i) => (
      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <input placeholder="snake_case_key" value={it.key || ''} onChange={e => onUpdate(i, 'key', e.target.value)} style={{ ...inp, flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
        <input placeholder="Human label" value={it.label || ''} onChange={e => onUpdate(i, 'label', e.target.value)} style={{ ...inp, flex: 2, fontSize: 12 }} />
        <button onClick={() => onRemove(i)} style={{ ...btnGhost, color: '#DC2626', fontSize: 11, padding: '4px 10px' }}>×</button>
      </div>
    ))}
    <button onClick={onAdd} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>+ Add item</button>
  </div>;
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnGhost = { background: 'transparent', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '10px 12px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '10px 12px', color: '#111827' };
